import { createTheme } from "@mui/material/styles";

// MUI expects a 25-entry shadow scale (indices 0–24). The theme previously
// defined only 3, so any `elevation={3..24}` resolved to `undefined` and the
// surface rendered with no shadow at all — which is why cards looked flat and
// failed to separate from the near-black background. Build a full dark-mode
// appropriate scale so elevation actually reads.
const buildShadows = () => {
  const shadows = ["none"];
  for (let i = 1; i <= 24; i += 1) {
    const y = i;
    const blur = Math.round(i * 1.6) + 1;
    const alpha = Math.min(0.6, 0.18 + i * 0.012).toFixed(2);
    const ambient = Math.min(0.4, 0.1 + i * 0.008).toFixed(2);
    shadows.push(
      `0px ${y}px ${blur}px rgba(0,0,0,${alpha}), 0px ${Math.round(
        y / 2
      )}px ${Math.round(blur / 2)}px rgba(0,0,0,${ambient})`
    );
  }
  return shadows;
};

export const themeConfig = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#F2E600", // Signal Yellow — the hybra brand accent, used as a solid
      // fill (buttons/toggles). Yellow is a *light* colour, so contrastText must
      // be near-black to stay legible on top (white text on yellow fails AA).
      light: "#F2E600", // Yellow is already light and reads well as a foreground
      // on the near-black surface (~15:1); no separate lighter tint needed.
      dark: "#E2D400", // Slightly deeper yellow for gradients/hover states
      // (referenced by button hovers, chart points, onboarding gradients).
      contrastText: "#0A0A0A", // Near-black text/icons on yellow surfaces.
    },
    secondary: {
      main: "#3A3A3D", // Steel Gray — secondary surfaces / strokes
      contrastText: "#FFFFFF", // Text on Secondary
    },
    tertiary: {
      main: "#7CD313", // Lime — re-anchored to the hybra scheme (was violet).
      contrastText: "#0A0A0A", // Text on Tertiary
    },
    background: {
      default: "#0D0D0F", // Near-black background
      paper: "#18181B", // Surface color
    },
    text: {
      primary: "#FFFFFF", // White
      secondary: "#A0A0A0", // Mid Gray
      disabled: "#6E6E73", // Dim Gray
    },
    error: {
      main: "#FF453A", // Hybra red
      contrastText: "#FFFFFF", // Text on Error
    },
    success: {
      main: "#7CD313", // Hybra lime-green
    },
    warning: {
      // Admin dashboards reference `warning.main` (Chip color, Alert borders).
      // Define it explicitly — hybra amber — rather than relying on MUI's silent
      // default (#ffa726). Dark text on amber is the correct contrast pairing.
      main: "#FF9F0A",
      contrastText: "#000000",
    },
    divider: "#2A2A2E", // Divider color
  },
  shadows: buildShadows(),
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: "3rem",
      lineHeight: 1.2,
      "@media (min-width:600px)": {
        fontSize: "3.75rem",
      },
      "@media (min-width:900px)": {
        fontSize: "4.5rem",
      },
    },
    h2: {
      fontWeight: 700,
      fontSize: "2.5rem",
      lineHeight: 1.2,
    },
    h3: {
      fontWeight: 600,
      fontSize: "2rem",
    },
    h4: {
      fontWeight: 600,
      fontSize: "1.5rem",
    },
    h5: {
      fontWeight: 600,
      fontSize: "1.25rem",
    },
    h6: {
      fontWeight: 600,
      fontSize: "1rem",
    },
  },
});

// import { createTheme } from "@mui/material/styles";

// export const themeConfig = createTheme({
//   palette: {
//     mode: "dark",
//     primary: {
//       main: "#000000", // Pure Black
//       contrastText: "#FFFFFF", // White text on black
//     },
//     secondary: {
//       main: "#333333", // Dark Gray
//       contrastText: "#FFFFFF", // White text on dark gray
//     },
//     tertiary: {
//       main: "#5b05f6",
//       contrastText: "#FFFFFF", // Text on Tertiary
//     },
//     background: {
//       default: "#FFFFFF", // White background
//       paper: "#F5F5F5", // Light gray surface
//     },
//     text: {
//       primary: "#000000", // Black
//       secondary: "#333333", // Dark Gray
//       disabled: "#888888", // Mid Gray
//       contrastText: "#FFFFFF", // White text on black
//     },
//     error: {
//       main: "#000000", // Black (for errors)
//       contrastText: "#FFFFFF", // White text on black
//     },
//     divider: "#DDDDDD", // Light Gray divider
//   },
//   typography: {
//     fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
//     h1: {
//       fontWeight: 700,
//     },
//     h2: {
//       fontWeight: 600,
//     },
//     button: {
//       textTransform: "none",
//       fontWeight: 500,
//     },
//   },
//   shape: {
//     borderRadius: 4,
//   },
//   shadows: [
//     "none",
//     "0px 1px 3px rgba(0, 0, 0, 0.1)",
//     "0px 2px 4px rgba(0, 0, 0, 0.1)",
//     "0px 3px 6px rgba(0, 0, 0, 0.1)",
//     // ... Add more shadow levels as needed
//   ],
//   components: {
//     MuiButton: {
//       styleOverrides: {
//         root: {
//           borderRadius: 4,
//         },
//         contained: {
//           boxShadow: "none",
//           "&:hover": {
//             boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
//           },
//         },
//       },
//     },
//   },
// });
